import { getCookieConsentCookieDomain, resolveCookieConsentDeployment } from '@repo/common/constants/cookie-consent';

import { setCookie } from './cookie';

export const MARKETING_STORAGE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export const META_PURCHASE_STORAGE_PREFIX = 'meta-purchase:';
export const SIGNUP_ATTRIBUTION_STORAGE_KEY = 'starthub-first-touch';
const META_EXTERNAL_REFERRER_STORAGE_KEYS = ['lastExternalReferrer', 'lastExternalReferrerTime'] as const;

interface RetainedMarker {
  createdAt: number;
}

function isRetainedMarker(value: unknown, now: number): value is RetainedMarker {
  if (typeof value !== 'object' || value === null || !('createdAt' in value)) return false;
  const createdAt = value.createdAt;
  return (
    typeof createdAt === 'number' &&
    Number.isFinite(createdAt) &&
    createdAt <= now &&
    now - createdAt < MARKETING_STORAGE_RETENTION_MS
  );
}

export function shouldTrackMetaPurchase(paymentId: string, now = Date.now()): boolean {
  const key = `${META_PURCHASE_STORAGE_PREFIX}${paymentId}`;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isRetainedMarker(parsed, now)) return false;
      localStorage.removeItem(key);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        !('createdAt' in parsed) ||
        typeof parsed.createdAt !== 'number' ||
        !Number.isFinite(parsed.createdAt) ||
        parsed.createdAt > now
      ) {
        return false;
      }
    }

    const probeKey = `${META_PURCHASE_STORAGE_PREFIX}storage-probe`;
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return true;
  } catch {
    // Without durable deduplication, fail closed rather than emit repeat purchase tracking.
    return false;
  }
}

export function markMetaPurchaseTracked(paymentId: string, now = Date.now()): void {
  try {
    localStorage.setItem(`${META_PURCHASE_STORAGE_PREFIX}${paymentId}`, JSON.stringify({ createdAt: now }));
  } catch {
    // Tracking has already been queued; a denied write must not affect checkout completion.
  }
}

export function clearMarketingRetentionStorage(): void {
  try {
    localStorage.removeItem(SIGNUP_ATTRIBUTION_STORAGE_KEY);
    for (const key of META_EXTERNAL_REFERRER_STORAGE_KEYS) localStorage.removeItem(key);
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(META_PURCHASE_STORAGE_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    // Consent withdrawal must remain usable when browser storage is denied.
  }

  const hostname = window.location.hostname;
  const name = `starthub-signup-completed-${resolveCookieConsentDeployment(hostname)}`;
  const domain = getCookieConsentCookieDomain(hostname);
  try {
    setCookie(name, '', {
      domain,
      maxAgeSeconds: 0,
      path: '/',
      sameSite: 'Lax',
      secure: domain !== undefined,
    });
  } catch {
    // Cookie access can be denied independently of localStorage.
  }
}
